"use client";
/**
 * Svendetid vs. [competitor] — comparison page content.
 * Language-parametrized so /compare/svendetid-vs-x (da) and
 * /en/compare/svendetid-vs-x (en) share one source.
 * Uses the shared svendetid-ds design system (same nav + footer as
 * /features, /pricing, /about).
 *
 * Facts about competitors are sourced from their own public pricing/feature
 * pages at the time of writing (2026-07) and should be re-checked periodically
 * since third-party pricing can change without notice.
 */
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import {
  dsFontClass, dsTokensCss, Icon, Footer, type Lang,
} from "./svendetid-ds";
import LandingNav from "./landing-nav";

export type CompareValue = string | boolean | { da: string; en: string };

export type CompareRow = {
  label: { da: string; en: string };
  svendetid: CompareValue;
  competitor: CompareValue;
};

export type CompetitorProfile = {
  slug: string;
  name: string;
  homepage: string;
  startingPrice: { da: string; en: string };
  rows: CompareRow[];
  sourceNote: { da: string; en: string };
};

const CELL = ({ value, lang }: { value: CompareValue; lang: Lang }) => {
  if (typeof value === "boolean") {
    return value ? (
      <Icon name="checkmark-circle" size={20} color="var(--brand)" />
    ) : (
      <span style={{ color: "var(--text-faint)" }}>—</span>
    );
  }
  if (typeof value === "object") {
    return <span>{value[lang]}</span>;
  }
  return <span>{value}</span>;
};

const T = {
  da: {
    eyebrow: "Sammenligning",
    ctaH2: "Prøv Svendetid selv",
    ctaBody: "14 dages gratis prøveperiode. Ingen binding, ingen kreditkort krævet.",
    tryFree: "Prøv det gratis",
    seePricing: "Se priser",
    startingFrom: "fra",
    perMonth: "/ md.",
    vatNote: "Ekskl. moms",
    tableFeature: "Funktion",
    disclaimer: "Priser og funktioner for konkurrenten er baseret på offentligt tilgængelige oplysninger fra deres egen hjemmeside og kan have ændret sig siden.",
  },
  en: {
    eyebrow: "Comparison",
    ctaH2: "Try Svendetid yourself",
    ctaBody: "14-day free trial. No commitment, no credit card required.",
    tryFree: "Try it free",
    seePricing: "See pricing",
    startingFrom: "from",
    perMonth: "/ mo.",
    vatNote: "Excl. VAT",
    tableFeature: "Feature",
    disclaimer: "The competitor's pricing and features are based on publicly available information from their own website and may have changed since.",
  },
} as const;

export default function CompareContent({ lang = "da", profile }: { lang?: Lang; profile: CompetitorProfile }) {
  const t = T[lang];
  const home = lang === "en" ? "/en" : "/";
  const pricingHref = lang === "en" ? "/en/pricing" : "/pricing";
  const pagePath = `/compare/svendetid-vs-${profile.slug}`;

  const h1 = lang === "en"
    ? `Svendetid vs. ${profile.name}`
    : `Svendetid vs. ${profile.name}`;
  const sub = lang === "en"
    ? `A feature-by-feature look at how Svendetid compares to ${profile.name} for Danish trade businesses.`
    : `Et funktion-for-funktion overblik over, hvordan Svendetid sammenligner sig med ${profile.name} for danske håndværksvirksomheder.`;

  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{`html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}`}</style>

      <LandingNav lang={lang} />

      {/* ===== HERO ===== */}
      <section style={{ background: "var(--green-900)", color: "var(--text-on-hero)", paddingTop: 64 }}>
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "104px 32px 96px", textAlign: "center" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#F4C8A6" }}>{t.eyebrow}</div>
          <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 46, lineHeight: 1.1, letterSpacing: "-0.025em", margin: "18px 0 0", color: "#fff", textWrap: "balance" }}>{h1}</h1>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "36em", margin: "22px auto 0" }}>{sub}</p>
        </div>
      </section>

      {/* ===== PRICE STRIP ===== */}
      <section style={{ padding: "48px 0 0" }}>
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "0 32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ background: "var(--surface-card)", border: "1.5px solid var(--green-700)", borderRadius: 18, padding: 24, textAlign: "center" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--accent-strong)", marginBottom: 8 }}>Svendetid</div>
            <div style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 30, color: "var(--green-900)" }}>kr 99<span style={{ fontFamily: "var(--font-mono)", fontSize: 14, color: "var(--text-muted)" }}> {t.perMonth}</span></div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{t.vatNote}</div>
          </div>
          <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 18, padding: 24, textAlign: "center" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>{profile.name}</div>
            <div style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 30, color: "var(--green-900)" }}>{t.startingFrom} {profile.startingPrice[lang]}</div>
          </div>
        </div>
      </section>

      {/* ===== COMPARISON TABLE ===== */}
      <section style={{ padding: "48px 0 96px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 20, overflow: "hidden", boxShadow: "0 6px 16px rgba(16,33,27,0.06)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr" }}>
              <div style={{ padding: "18px 20px", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" }}>{t.tableFeature}</div>
              <div style={{ padding: "18px 12px", textAlign: "center", background: "var(--green-900)" }}>
                <div style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 15, color: "#fff" }}>Svendetid</div>
              </div>
              <div style={{ padding: "18px 12px", textAlign: "center", borderLeft: "1px solid var(--hairline)" }}>
                <div style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 15, color: "var(--green-900)" }}>{profile.name}</div>
              </div>
            </div>
            {profile.rows.map((row, i) => (
              <div
                key={row.label.da}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.6fr 1fr 1fr",
                  borderTop: "1px solid var(--hairline)",
                  background: i % 2 === 1 ? "var(--green-50)" : "transparent",
                }}
              >
                <div style={{ padding: "14px 20px", fontSize: 14, color: "var(--text-body)" }}>{row.label[lang]}</div>
                <div style={{ padding: "14px 12px", textAlign: "center", fontSize: 13, color: "var(--text-body)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CELL value={row.svendetid} lang={lang} />
                </div>
                <div style={{ padding: "14px 12px", textAlign: "center", fontSize: 13, color: "var(--text-body)", display: "flex", alignItems: "center", justifyContent: "center", borderLeft: "1px solid var(--hairline)" }}>
                  <CELL value={row.competitor} lang={lang} />
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text-faint)", marginTop: 16 }}>
            {t.disclaimer} <Link href={profile.homepage} target="_blank" rel="noopener noreferrer nofollow" style={{ color: "var(--text-faint)", textDecoration: "underline" }}>{profile.name}</Link>. {profile.sourceNote[lang]}
          </p>
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section style={{ padding: "0 0 96px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ background: "var(--green-900)", borderRadius: 26, padding: "64px 48px", textAlign: "center" }}>
            <img src="/svendetid-logo.png" alt="" style={{ width: 64, height: 64, margin: "0 auto 18px", display: "block", opacity: 0.96 }} />
            <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 38, letterSpacing: "-0.02em", lineHeight: 1.08, margin: "0 auto", maxWidth: "18em", color: "#fff" }}>{t.ctaH2}</h2>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "30em", margin: "16px auto 0" }}>{t.ctaBody}</p>
            <div className="ds-cta-row" style={{ display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center", marginTop: 32 }}>
              <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-accent" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--accent)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(194,65,12,0.32)" }}>{t.tryFree}</button></SignUpButton>
              <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref={pagePath} enHref={`/en${pagePath}`} />
    </div>
  );
}
